// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// --- 3rd Party
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { LanguagesBaseService } from './languages-base.service';
import { AppService } from '../app.service';
import { ApplicationError, Constants, ErrorMessage, LanguageModel, ToastModel } from '../../../models';
import { Utils } from '../../../shared';
import { environment } from '../../../../environments/environment';
import { EnvService } from '../env.service';
import { OidcService } from '../account/oidc.service'

// #endregion

@Injectable({
  providedIn: 'root'
})
export class LanguagesService extends LanguagesBaseService {
  private httpClient: HttpClient;

  constructor(
    private handler: HttpBackend,
    private appService: AppService,
    private envService: EnvService,
    private translate: TranslateService,
    private oidcService: OidcService,
    private http: HttpClient,
  ) {
    super();

    this.httpClient = new HttpClient(handler);
  }

  initialiseLanguage(selectedLocale: string | undefined): void {
    if (this._isInitialised) {
      return;
    }

    if (!selectedLocale) {
      const savedLanguage = this._getSavedLanguage();
      if (savedLanguage) {
        selectedLocale = this._getLocale(savedLanguage as LanguageModel);
      }
    }
    this._initialisedLocale = selectedLocale ?? Constants.DEFAULT_LOCALE;
    this._isInitialised = true;

    if (this._languagesList$.value) {
      const selectedLanguage = this._languages$.value.find(l => {
        return this._initialisedLocale === l.cultureCode ? `${l.isoTwoLetterName}-${l.cultureCode}` : l.isoTwoLetterName;
      });
      if (selectedLanguage) {
        this._selectedLanguage$.next(selectedLanguage as LanguageModel);
      }
    } else {
      this._selectedLanguage$.next(Constants.DEFAULT_LANGUAGE as LanguageModel);
    }

    this._setSavedLanguage(this._selectedLanguage$.value);
    this.translate.use(this._initialisedLocale);
  }

  loadLanguages(): void {
    const uri =
    environment.apiConfig.surveyApiUrl + environment.apiConfig.languagesApi;

    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Client-Id': this.envService.clientId,
      'X-Version': environment.apiVersion,
    }

    this.httpClient
      .get<LanguageModel[]>(uri, { headers })
      .pipe(catchError(this._handleError.bind(this)))
      .subscribe(languages => {
        this._languages$.next(languages);
        this._processLanguages(languages);
        if (!this._isInitialised && (this._initialisedLocale !== null)) {
          this.initialiseLanguage(this._initialisedLocale);
        }
      });
  }

  updateUserLanguage(newLanguage: LanguageModel, surveyId?: string): void {
    if (Utils.isNull(newLanguage)) {
      return;
    }

    if (this._isNewLanguageTheSameAsCurrentLanguage(newLanguage)) {
      return;
    }

    this.oidcService.isAuthenticated().then((isAuth) => {
      if (isAuth) {

      const url =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.languagesApi;
      this.http
        .put<LanguageModel>(url, newLanguage)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._handleError.bind(this))
        )
        .subscribe(langResult => {
          this._isInitialised = true;
          localStorage.setItem(
            Constants.SELECTED_LANGUAGE_LOCAL_STORAGE_KEY,
            JSON.stringify(newLanguage)
          );


          this.oidcService.identity$.subscribe((user) => {
            if (user !== null && user !== undefined) {
              this.oidcService.signInSilent();
            }
          });
        });
      }

      this._selectedLanguage$.next(newLanguage);
      this._setSavedLanguage(this._selectedLanguage$.value);
      this.translate.use(this._getLocale(newLanguage));
    });

  }

  private _handleError(err: any): Observable<any> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'LanguagesService',
      false,
      err);

    this.appService.setLastError(error);

    return of([Constants.DEFAULT_LANGUAGE] as LanguageModel[]);
  }

  private _isNewLanguageTheSameAsCurrentLanguage(newLanguage: LanguageModel): boolean {
    const currentLanguage = this._getSavedLanguage();
    return !!(currentLanguage && newLanguage.id === currentLanguage.id);
  }
}
